import React, { useMemo } from "react";
import moment from "moment";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { useNewMembersDetail } from "./hooks/useNewMembersDetail";
import Header from "../../../components/Header";
import { Table } from "../../../../../shared/Table/Table";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { LoadMoreObserver } from "../../../../../shared/LoadMoreObserver/LoadMoreObserver";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { formatCurrency } from "../../../../../utilities/general";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const NewMembersDetail = () => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();

  const requestParams = {
    clinic_id:
      selectedClinic?.value?.value !== 0 ? [selectedClinic?.value?.value] : [],
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useNewMembersDetail(requestParams);

  const report = useMemo(() => {
    return data?.pages?.map((page) => page.report).flat() || [];
  }, [data]);

  const apiClinics = useMemo(() => {
    return data?.pages?.[0]?.clinics || [];
  }, [data]);

  const tableColumns = [
    {
      data: "Patient Name",
      accessor: "patientName",
    },
    {
      data: "Email",
      accessor: "email",
    },
    {
      data: "Phone",
      accessor: "phone",
    },
    {
      data: "Membership Type",
      accessor: "membershipType",
    },
    {
      data: "Signup Date",
      accessor: "signupDate",
    },
    {
      data: "Signup Clinic",
      accessor: "signupClinic",
    },
    {
      data: "Referring Provider",
      accessor: "referringProvider",
    },
    {
      data: "Initial Payment",
      accessor: "initialPayment",
    },
    {
      data: "Setup Fee",
      accessor: "setupFee",
    },
    {
      data: "Total Initial Cost",
      accessor: "totalInitialCost",
    },
    {
      data: "Payment Method",
      accessor: "paymentMethod",
    },
    {
      data: "Status",
      accessor: "status",
    },
  ];

  const tableData = useMemo(() => {
    return report.map((item) => ({
      key: item.subscription_id,
      patientName: item.patient_name || "-",
      email: item.patient_email || "-",
      phone: item.patient_phone || "-",
      membershipType: item.membership_plan || "-",
      signupDate: item.start_date
        ? moment(item.start_date).format("MM/DD/YYYY")
        : "-",
      signupClinic: item.clinic_name || "-",
      referringProvider: item.sold_by || "-",
      initialPayment: formatCurrency(item.first_invoice_amount || 0),
      setupFee: formatCurrency(item.one_time_setup_fee || 0),
      totalInitialCost: formatCurrency(
        (item.first_invoice_amount || 0) + (item.one_time_setup_fee || 0),
      ),
      paymentMethod: "-", // Not in API response
      status: item.current_status || "-",
    }));
  }, [report]);

  const clinics = useMemo(() => {
    const clinicOptions = [{ label: "All Clinics", value: 0 }];
    if (apiClinics?.length) {
      clinicOptions.push(
        ...apiClinics.map((clinic) => ({
          label: clinic.clinic_name,
          value: clinic.id,
        })),
      );
    }
    return clinicOptions;
  }, [apiClinics]);

  return (
    <div className="flex-col gap-16">
      <Header
        title="New Members Detail"
        clinics={clinics}
        selectedClinic={selectedClinic}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getNewMembersDetail()}
        requestParams={requestParams}
      />

      <Box>
        {isLoading && (
          <div className={classes.tableContainer}>
            <Skeleton count={10} height={40} borderRadius="0px" />
          </div>
        )}

        {error && (
          <Empty
            position="center"
            title="API Error"
            description={`Failed to load new members data: ${
              error.message || "Unknown error"
            }`}
          />
        )}

        {!isLoading && !error && tableData.length > 0 && (
          <div className={classes.tableContainer} style={{ overflowX: "auto" }}>
            <Table
              cols={tableColumns}
              data={tableData}
              colClassName="bordersides"
              headColClassName={classes.tableHeaderColumn}
            />
            {hasNextPage && (
              <LoadMoreObserver
                noPaddingBottom
                loadMore={fetchNextPage}
                isLoading={isFetchingNextPage}
                hasMore={hasNextPage}
              />
            )}
          </div>
        )}

        {!isLoading && !error && tableData.length === 0 && (
          <Empty
            position="center"
            title="No new members data found"
            description="Try adjusting your date range or clinic selection"
          />
        )}
      </Box>
    </div>
  );
};

export default NewMembersDetail;
